import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lightbulb, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'ccfs', label: 'CCFS' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'practice', label: 'Practice Growth' },
  { key: 'general', label: 'General' },
];

export default function AdminTipsPage() {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [creating, setCreating] = useState(false);

  const token = localStorage.getItem('ratnatraya_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchTips = () => {
    fetch(`${API_URL}/api/admin/tips`, { headers })
      .then(r => r.json())
      .then(data => setTips(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load tips'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTips(); }, []); // eslint-disable-line

  const handleCreate = async () => {
    if (!newTitle.trim() || !newContent.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/tips`, {
        method: 'POST', headers,
        body: JSON.stringify({ title: newTitle, content: newContent, category: newCategory, published: true }),
      });
      if (res.ok) {
        toast.success('Tip published!');
        setNewTitle('');
        setNewContent('');
        setNewCategory('general');
        fetchTips();
      } else {
        toast.error('Failed to create tip');
      }
    } catch { toast.error('Failed to create tip'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/api/admin/tips/${id}`, { method: 'DELETE', headers });
      toast.success('Tip deleted');
      setTips(prev => prev.filter(t => t.id !== id));
    } catch { toast.error('Failed to delete tip'); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="admin-tips-page">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
          <Lightbulb className="w-5 h-5" /> Partner Tips
        </h1>
        <p className="text-xs text-slate-500 mt-1">Create daily tips and updates for R-PACE partners. The latest published tip shows on their Dashboard.</p>
      </div>

      {/* Create New Tip */}
      <Card className="mb-6 border-l-4 border-l-amber-500" data-testid="create-tip-card">
        <CardContent className="p-5 space-y-3">
          <p className="text-xs font-semibold text-slate-600 uppercase">New Tip</p>
          <Input
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Tip title (e.g. 'CCFS 2026 deadline extended')"
            className="text-sm"
            data-testid="tip-title-input"
          />
          <textarea
            value={newContent}
            onChange={e => setNewContent(e.target.value)}
            placeholder="Tip content (2-3 lines max, partners see this on their dashboard)"
            className="w-full text-sm border border-slate-200 rounded-lg p-3 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
            data-testid="tip-content-input"
          />
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-slate-500 font-medium">Category:</p>
            {CATEGORIES.map(c => (
              <button key={c.key} onClick={() => setNewCategory(c.key)}
                className={`text-[10px] font-medium px-2 py-1 rounded-full border transition-all ${newCategory === c.key ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
              >{c.label}</button>
            ))}
          </div>
          <Button onClick={handleCreate} disabled={creating} className="w-full bg-[#1e3a5f] hover:bg-[#152d4a] text-sm gap-1.5" data-testid="publish-tip-btn">
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Publish Tip
          </Button>
        </CardContent>
      </Card>

      {/* Existing Tips */}
      <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Published Tips ({tips.length})</p>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : tips.length === 0 ? (
        <Card><CardContent className="p-8 text-center"><p className="text-sm text-slate-400">No tips yet. Create your first one above.</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {tips.map(tip => (
            <Card key={tip.id} data-testid={`tip-${tip.id}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-800">{tip.title}</p>
                    <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{tip.category}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{tip.content}</p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {new Date(tip.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-8 w-8 p-0 shrink-0"
                  onClick={() => handleDelete(tip.id)} data-testid={`delete-tip-${tip.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
